import React, { useState, useEffect, useMemo } from 'react';
import { CalendarPlus, X, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';

interface ProlongerModalProps {
    isOpen: boolean;
    type?: 'subscription' | 'seance';
    subscription: any | null;
    onClose: () => void;
    onSuccess: (receipt: any) => void;
}

const PAYMENT_METHODS = [
    { value: 'Espèces', label: 'Espèces' },
    { value: 'Wave', label: 'Wave' },
    { value: 'Orange Money', label: 'Orange Money' },
    { value: 'MTN Money', label: 'MTN Money' },
    { value: 'Moov Money', label: 'Moov Money' },
    { value: 'Carte Bancaire', label: 'Carte Bancaire' },
    { value: 'Virement', label: 'Virement' },
    { value: 'Autre', label: 'Autre' },
];

const addPeriod = (startDateStr: string, periodicity: string, q: number) => {
    if (!startDateStr) return '';
    const d = new Date(startDateStr);
    const count = Math.max(1, q);
    switch (periodicity?.toLowerCase()) {
        case 'seance':
            d.setDate(d.getDate() + count);
            break;
        case 'hebdomadaire':
            d.setDate(d.getDate() + (7 * count));
            break;
        case 'trimestriel':
            d.setMonth(d.getMonth() + (3 * count));
            break;
        case 'semestriel':
            d.setMonth(d.getMonth() + (6 * count));
            break;
        case 'annuel':
            d.setFullYear(d.getFullYear() + count);
            break;
        case 'mensuel':
        default:
            d.setMonth(d.getMonth() + count);
            break;
    }
    return d.toISOString().split('T')[0];
};

const ProlongerModal: React.FC<ProlongerModalProps> = ({
    isOpen,
    type = 'subscription',
    subscription,
    onClose,
    onSuccess
}) => {
    const isAbo = type === 'subscription';
    const [quantity, setQuantity] = useState(1);
    const [newSessionDate, setNewSessionDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Espèces');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const currentDate = isAbo ? subscription?.end_date : subscription?.session_date;

    useEffect(() => {
        if (isOpen && subscription) {
            setQuantity(1);
            setPaymentMethod('Espèces');
            setError('');
            if (!isAbo && subscription.session_date) {
                setNewSessionDate(new Date(subscription.session_date).toISOString().split('T')[0]);
            }
        }
    }, [isOpen, subscription, isAbo]);

    const periodicity = useMemo(() => {
        if (!isAbo) return 'séance';
        return subscription?.forfait_periodicity || subscription?.periodicity || 'mensuel';
    }, [subscription, isAbo]);

    const unitPrice = useMemo(() => {
        if (!subscription) return 0;
        return Number(subscription.forfait_unit_price || subscription.price_without_inscription || subscription.price_with_inscription || subscription.expected_price || subscription.amount || 0);
    }, [subscription]);

    const addedPrice = useMemo(() => {
        return unitPrice * Math.max(1, quantity);
    }, [unitPrice, quantity]);

    const calculatedNewEndDate = useMemo(() => {
        if (!currentDate) return '';
        if (isAbo) {
            return addPeriod(currentDate, periodicity, quantity);
        } else {
            return newSessionDate || addPeriod(currentDate, 'seance', quantity);
        }
    }, [currentDate, periodicity, quantity, isAbo, newSessionDate]);

    if (!isOpen || !subscription) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (quantity < 1) {
            setError('La quantité doit être au moins de 1.');
            return;
        }

        try {
            setSubmitting(true);
            const clientName = `${subscription.first_name || ''} ${subscription.last_name || ''}`.trim();

            if (isAbo) {
                await api.put(`/subscriptions/${subscription.id}/extend`, {
                    new_end_date: calculatedNewEndDate,
                    added_price: addedPrice,
                    payment_method: paymentMethod,
                    quantity,
                    unit_price: unitPrice
                });

                const receiptData = {
                    clientName,
                    planName: subscription.forfait_name || subscription.type_name || 'Abonnement (Prolongation)',
                    periodicity: `${periodicity} (+${quantity})`,
                    duration: subscription.forfait_duration || '',
                    startDate: subscription.start_date ? new Date(subscription.start_date).toLocaleDateString('fr-FR') : '',
                    endDate: calculatedNewEndDate ? new Date(calculatedNewEndDate).toLocaleDateString('fr-FR') : '',
                    quantity,
                    paymentMethod,
                    total: addedPrice
                };

                onSuccess(receiptData);
            } else {
                await api.put(`/seances/${subscription.id}/extend`, {
                    new_session_date: calculatedNewEndDate,
                    added_price: addedPrice,
                    payment_method: paymentMethod,
                    quantity,
                    unit_price: unitPrice
                });

                const receiptData = {
                    clientName,
                    forfaitName: subscription.forfait_name || 'Séance (Prolongation)',
                    sessionDate: calculatedNewEndDate ? new Date(calculatedNewEndDate).toLocaleDateString('fr-FR') : '',
                    sessionTime: subscription.session_time?.substring(0, 5) || '',
                    quantity,
                    paymentMethod,
                    total: addedPrice
                };

                onSuccess(receiptData);
            }

            onClose();
        } catch (err: any) {
            console.error('Erreur prolongation:', err);
            setError(err.response?.data?.error || 'Erreur lors de la prolongation.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-emerald-50 to-teal-50">
                    <div className="flex items-center gap-2.5 text-emerald-800">
                        <div className="p-2 bg-emerald-100 rounded-xl text-emerald-700">
                            <CalendarPlus size={22} />
                        </div>
                        <div>
                            <h3 className="text-base font-black text-gray-900 leading-tight">
                                Prolonger {isAbo ? "l'abonnement" : "la séance"}
                            </h3>
                            <p className="text-xs text-emerald-700 font-medium">
                                Extension de validité et quantité sans créer de doublon
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-white/80 transition-colors cursor-pointer"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-center gap-2">
                            <AlertCircle size={16} className="shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Fiche Client & Actuel */}
                    <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 text-xs space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500 font-medium">Client :</span>
                            <span className="font-bold text-gray-900 text-sm">
                                {subscription.first_name} {subscription.last_name}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500 font-medium">Forfait :</span>
                            <span className="font-semibold text-blue-700">
                                {subscription.forfait_name || subscription.type_name || (isAbo ? 'Abonnement' : 'Séance')}
                            </span>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t border-slate-200">
                            <span className="text-gray-500 font-medium">
                                {isAbo ? "Date de fin actuelle :" : "Date de séance actuelle :"}
                            </span>
                            <span className="font-bold text-amber-800 bg-amber-100/70 px-2 py-0.5 rounded">
                                {currentDate ? new Date(currentDate).toLocaleDateString('fr-FR') : '-'}
                            </span>
                        </div>
                    </div>

                    {/* Report de date si séance */}
                    {!isAbo && (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">
                                Nouvelle date de validité / séance
                            </label>
                            <input
                                type="date"
                                value={newSessionDate}
                                onChange={(e) => setNewSessionDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                            />
                        </div>
                    )}

                    {/* Quantité à prolonger */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">
                            {isAbo ? "Durée supplémentaire à ajouter" : "Nombre de séances à ajouter"}
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="number"
                                min="1"
                                max="24"
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                className="w-28 px-3 py-2.5 border border-gray-300 rounded-lg text-base font-bold text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                            />
                            <span className="text-xs text-gray-500 font-medium">
                                x {periodicity} (ex: +{quantity} {periodicity})
                            </span>
                        </div>
                    </div>

                    {/* Mode de paiement */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">
                            Mode de paiement pour la prolongation <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 font-medium outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                            required
                        >
                            {PAYMENT_METHODS.map((m) => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Récapitulatif dynamique */}
                    <div className="p-4 bg-emerald-50/80 border border-emerald-200/80 rounded-xl text-xs space-y-2">
                        <div className="flex items-center justify-between text-gray-700">
                            <span>Prix unitaire du forfait :</span>
                            <span className="font-semibold text-gray-900">{unitPrice.toLocaleString()} FCFA</span>
                        </div>
                        {quantity > 1 && (
                            <div className="flex items-center justify-between text-gray-700">
                                <span>Quantité ajoutée :</span>
                                <span className="font-semibold text-emerald-800">x{quantity} ({periodicity})</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between pt-1 border-t border-emerald-200/60">
                            <span className="font-semibold text-gray-700">
                                {isAbo ? "Nouvelle date d'expiration :" : "Nouvelle date de séance :"}
                            </span>
                            <span className="font-bold text-emerald-900 bg-emerald-200/60 px-2 py-0.5 rounded text-xs">
                                {calculatedNewEndDate ? new Date(calculatedNewEndDate).toLocaleDateString('fr-FR') : '-'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-emerald-200 font-bold">
                            <span className="text-sm text-gray-900">Montant à encaisser :</span>
                            <span className="text-lg font-black text-emerald-700">
                                {addedPrice.toLocaleString()} FCFA
                            </span>
                        </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 px-4 border border-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                            {submitting ? (
                                <span>Enregistrement...</span>
                            ) : (
                                <>
                                    <CheckCircle size={16} />
                                    <span>Valider & Encaisser</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProlongerModal;
